<template>
  <div class="dashboard-wrapper">
    <!-- Step 1: AR Property Owner Selection (if needed) -->
    <PropertyOwnerSelector
      v-if="showPropertyOwnerSelector"
      @select="handlePropertyOwnerSelect"
    />

    <!-- Step 2: Dashboard (for Property Owner or AR after selection) -->
    <template v-else>
      <Sidebar
        :menu-items="menuItemsForRole"
        @toggle="handleSidebarToggle"
        @select="handleMenuSelect"
      />

      <div
        class="dashboard-content"
        :class="{ 'sidebar-collapsed': isSidebarCollapsed }"
      >
        <!-- Dashboard Header -->
        <DashboardHeader :current-user="currentUser" />

        <div
          class="container is-widescreen"
          style="padding: 1rem;"
        >
          <!-- Content sections based on selected menu item -->
          <section v-if="selectedSection === 'applications'">
            <!-- Selected Property Owner Info (AR only) -->
            <div
              v-if="currentUser?.qbUser?.role === 'ar' && selectedPropertyOwner"
              class="box selected-owner-banner"
            >
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <div>
                      <h3 class="heading">
                        Viewing Applications for
                      </h3>
                      <p class="title is-5">
                        {{ selectedPropertyOwner.property_owner_org_name }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <button
                      class="button is-primary is-outlined"
                      @click="changePropertyOwner"
                    >
                      <span class="icon">
                        <i class="fas fa-exchange-alt" />
                      </span>
                      <span>Switch Owner</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div
              v-if="loadingApplications"
              class="loading-container"
            >
              <div class="loading-spinner">
                <i class="fas fa-spinner fa-spin fa-2x" />
              </div>
              <p class="has-text-grey">
                Loading applications...
              </p>
            </div>

            <ApplicationsTable
              v-else
              @row-click="handleApplicationClick"
              @check-status="handleCheckStatus"
            />
          </section>

          <section v-if="selectedSection === 'properties'">
            <!-- Selected Property Owner Info (AR only - shows only on Properties & Banking tab) -->
            <div
              v-if="currentUser?.qbUser?.role === 'ar' && selectedPropertyOwner"
              class="box selected-owner-banner"
            >
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <div>
                      <h3 class="heading">
                        Viewing Properties and Bank Info for
                      </h3>
                      <p class="title is-5">
                        {{ selectedPropertyOwner.property_owner_org_name }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <button
                      class="button is-primary is-outlined"
                      @click="changePropertyOwner"
                    >
                      <span class="icon">
                        <i class="fas fa-exchange-alt" />
                      </span>
                      <span>Switch Owner</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <callout type="warning">If a bank account status shows 'Expired', the property owner will need to upload all required documents for the bank account to continue receiving payments.</callout>

            <!-- Loading State for Properties -->
            <div
              v-if="loadingProperties"
              class="loading-container"
            >
              <div class="loading-spinner">
                <i class="fas fa-spinner fa-spin fa-2x" />
              </div>
              <p class="has-text-grey">
                Loading properties...
              </p>
            </div>

            <PropertiesTable
              v-else
              @row-click="handlePropertyClick"
              @edit="handleEditProperty"
              @add-property="handleAddProperty"
            />

            <!-- Loading State for Payments -->
            <div
              v-if="loadingPayments"
              class="loading-container"
            >
              <div class="loading-spinner">
                <i class="fas fa-spinner fa-spin fa-2x" />
              </div>
              <p class="has-text-grey">
                Loading banking information...
              </p>
            </div>

            <PaymentsTable
              v-else
              @row-click="handlePaymentClick"
              @edit="handleEditPayment"
              @add-payment="handleAddPayment"
              @set-primary="handleSetPrimaryPayment"
            />
          </section>

          <section v-if="selectedSection === 'representatives'">
            <AuthorizedRepsTable
              @row-click="handleRepClick"
              @edit="handleEditRep"
              @remove="handleRemoveRep"
              @add-rep="handleAddRep"
            />
          </section>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Sidebar from "./components/Sidebar.vue";
import DashboardHeader from "./components/DashboardHeader.vue";
import ApplicationsTable from "./components/ApplicationsTable.vue";
import PropertiesTable from "./components/PropertiesTable.vue";
import PaymentsTable from "./components/PaymentsTable.vue";
import AuthorizedRepsTable from "./components/AuthorizedRepsTable.vue";
import PropertyOwnerSelector from "./components/PropertyOwnerSelector.vue";

export default {
  name: "Dashboard",
  components: {
    Sidebar,
    DashboardHeader,
    ApplicationsTable,
    PropertiesTable,
    PaymentsTable,
    AuthorizedRepsTable,
    PropertyOwnerSelector,
  },
  data() {
    return {
      selectedPropertyOwner: null, // For ARs only
      isSidebarCollapsed: false,
      selectedSection: "applications",
    };
  },
  computed: {
    currentUser() {
      // Get authenticated user info from Vuex auth store
      return this.$store.getters['auth/userInfo'];
    },
    loadingApplications() {
      return this.$store.getters['dashboard/loadingApplications'];
    },
    loadingProperties() {
      return this.$store.getters['dashboard/loadingProperties'];
    },
    loadingPayments() {
      return this.$store.getters['dashboard/loadingPayments'];
    },
    menuItemsForRole() {
      const baseItems = [
        {
          id: "applications",
          label: "Applications",
          icon: "fas fa-file-alt",
          link: "#applications",
        },
        {
          id: "properties",
          label: "Properties & Banking",
          icon: "fas fa-building",
          link: "#properties",
        },
      ];

      // Property Owners get the Authorized Representatives menu item
      if (this.currentUser?.qbUser?.role === 'property_owner') {
        baseItems.push({
          id: "representatives",
          label: "Authorized Representatives",
          icon: "fas fa-user-tie",
          link: "#representatives",
        });
      }

      return baseItems;
    },
    showPropertyOwnerSelector() {
      // Show property owner selector for Authorized Representatives (AR) who haven't selected an owner yet
      return this.currentUser?.qbUser?.role === 'ar' && !this.selectedPropertyOwner;
    },
  },
  mounted() {
    this.$store.dispatch('dashboard/fetchAllData');
  },
  methods: {
    handleSidebarToggle(isCollapsed) {
      this.isSidebarCollapsed = isCollapsed;
    },
    handleMenuSelect(sectionId) {
      this.selectedSection = sectionId;
    },
    async handlePropertyOwnerSelect(owner) {
      console.log("Property owner selected:", owner);
      this.selectedPropertyOwner = owner;

      // Fetch properties and payments for the selected property owner's organization
      const orgRid = owner.related_organization;
      const ar_email = this.currentUser?.qbUser?.email;

      await Promise.all([
        this.$store.dispatch('dashboard/fetchProperties', {
          organization_rid: orgRid,
          ar_email,
        }),
        this.$store.dispatch('dashboard/fetchPayments', orgRid),
        this.$store.dispatch('dashboard/fetchARApplicationsForSelectedOwner', orgRid),
        this.$store.commit('dashboard/setSelectedPropertyOwner', this.selectedPropertyOwner),
      ]);
    },
    changePropertyOwner() {
      this.selectedPropertyOwner = null;
      this.selectedSection = "applications";
    },
    handleApplicationClick(application) {
      console.log("Application clicked:", application);
      // TODO: Navigate to application detail page
      // this.$router.push(`/dashboard/applications/${application.id}`);
    },
    handleCheckStatus(application) {
      console.log("Check status for:", application);
      // TODO: Open status modal or navigate to status page
    },
    handlePropertyClick(property) {
      console.log("Property clicked:", property);
      // TODO: Navigate to property detail page
    },
    handleEditProperty(property) {
      console.log("Edit property:", property);
      // TODO: Open edit property modal
    },
    handleAddProperty() {
      console.log("Add new property");
      // TODO: Open add property modal
    },
    handleRepClick(rep) {
      console.log("Rep clicked:", rep);
      // TODO: Navigate to rep detail page or open modal
    },
    handleEditRep(rep) {
      console.log("Edit rep:", rep);
      // TODO: Open edit rep modal
    },
    handleRemoveRep(rep) {
      console.log("Remove rep:", rep);
      // TODO: API call to remove rep, then refresh list
    },
    handleAddRep() {
      console.log("Add new rep");
      // TODO: Open add rep modal
    },
    handlePaymentClick(payment) {
      console.log("Payment clicked:", payment);
      // TODO: Navigate to payment detail page or open modal
    },
    handleEditPayment(payment) {
      console.log("Edit payment:", payment);
      // TODO: Open edit payment modal
    },
    handleAddPayment() {
      console.log("Add new payment method");
      // TODO: Open add payment modal
    },
    handleSetPrimaryPayment(payment) {
      console.log("Set primary payment:", payment);
      // TODO: API call to set primary payment method
    },
  },
};
</script>

<style scoped>
/* Dashboard layout - minimal custom styling needed */
.dashboard-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background: #f0f0f0; /* Ghost Gray - Philly Style Guide */
  overflow: hidden;
}

.dashboard-content {
  margin-left: 260px;
  margin-top: 70px; /* Account for fixed header */
  padding: 0;
  transition: margin-left 0.3s ease;
  height: calc(100vh - 70px); /* Subtract header height */
  overflow-y: auto;
}

.dashboard-content.sidebar-collapsed {
  margin-left: 0;
}

.dashboard-content.sidebar-collapsed .container.is-widescreen {
  max-width: none !important;
  margin-left: 1rem !important;
  margin-right: 1rem !important;
  padding: 2rem !important;
}

/* Selected Owner Banner (AR only) */
.selected-owner-banner {
  margin-bottom: 2rem;
  background: #daedfe; /* Light Ben Franklin Blue - Philly Style Guide */
  border-left: 4px solid #2176d2; /* Ben Franklin Blue - Philly Style Guide */
  padding: 0.5rem !important
}
.heading {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
  margin-bottom: 0.25rem;
  font-weight: bold;
}

/* Section spacing */
section {
  margin-bottom: 2rem;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  color: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
  margin-bottom: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    margin-top: 60px; /* Account for smaller header on mobile */
    padding: 0;
    height: calc(100vh - 60px); /* Subtract smaller header height */
  }

  .dashboard-content.sidebar-collapsed {
    margin-left: 160px;
  }
}
</style>
